/**
 * Admin Homeworks Page
 *
 * Page for managing the Homework / Study Planner link of each level (Director
 * only). The link appears in the "Homework" card on the student's Lessons page.
 */

import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Spinner } from "../../components/ui";
import { useAdminLevels, useUpdateLevel } from "../../hooks/useAdmin";
import type { AdminLevel } from "../../types/admin";

export function AdminHomeworksPage() {
  const { data: levels, isLoading } = useAdminLevels();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <Spinner size="lg" />
      </div>
    );
  }

  const sortedLevels = [...(levels ?? [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/admin"
            className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 text-sm mb-2 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back to Dashboard
          </Link>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-earth-800">
            Manage Homeworks
          </h1>
          <p className="text-earth-700 text-sm mt-1">
            Set the Homework / Study Planner link of each level — it appears in
            the "Homework" card on the student's Lessons page.
          </p>
        </div>

        {/* List */}
        <div className="bg-white rounded-xl border border-earth-200 overflow-hidden">
          <div className="divide-y divide-earth-100">
            {sortedLevels.map((level) => (
              <LevelHomeworkRow key={level.id} level={level} />
            ))}
          </div>
          {sortedLevels.length === 0 && (
            <div className="text-center py-12 text-earth-700">
              No levels yet. Create a level first.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Editable Homework / Study Planner link row for a single level.
 */
function LevelHomeworkRow({ level }: { level: AdminLevel }) {
  const updateLevel = useUpdateLevel();
  const [link, setLink] = useState(level.homeworkFileUrl ?? "");

  useEffect(() => {
    setLink(level.homeworkFileUrl ?? "");
  }, [level.homeworkFileUrl]);

  const handleSave = async () => {
    await updateLevel.mutateAsync({
      id: level.id,
      data: { homeworkFileUrl: link },
    });
  };

  const dirty = link !== (level.homeworkFileUrl ?? "");

  return (
    <div className="flex flex-col sm:flex-row sm:items-center gap-2 px-4 py-3">
      <span className="w-12 shrink-0 px-2 py-1 bg-olive-100 text-olive-700 rounded text-xs font-semibold text-center">
        {level.code}
      </span>
      <input
        type="url"
        value={link}
        onChange={(e) => setLink(e.target.value)}
        placeholder="https://drive.google.com/..."
        className="flex-1 px-3 py-2 border border-earth-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-olive-500/30 focus:border-olive-500"
      />
      <div className="flex items-center gap-2 shrink-0">
        {link ? (
          <a
            href={link}
            target="_blank"
            rel="noopener noreferrer"
            className="px-2 py-2 text-earth-500 hover:text-earth-700 text-sm inline-flex items-center gap-1"
            title="Open link"
          >
            <i className="ri-external-link-line"></i>
          </a>
        ) : null}
        <button
          onClick={handleSave}
          disabled={updateLevel.isPending || !dirty}
          className="px-4 py-2 bg-olive-600 hover:bg-olive-700 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 whitespace-nowrap"
        >
          {updateLevel.isPending ? "Saving..." : "Save"}
        </button>
      </div>
    </div>
  );
}

export default AdminHomeworksPage;
